/**
 * The walkthrough, as data (guide v2, the M4 portfolio surface).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The /guide route renders this module, and docs/user/TEST_GUIDE.md says the same thing
 * in the same order. There is no markdown tooling on the route: the steps are typed
 * content in src/guide/ so the page needs nothing but the design system. One action per
 * step, every step names its screen and what success looks like, and no step invents a
 * feature the surface does not have.
 *
 * The order is work order M4 section 10's demo journey, walked on the M4 surface: the
 * persona dashboards, the ITP approval that feeds the planner, the planned activity,
 * the conduct walk over the checklist, the finding the rejected line births, the
 * response and punch loops, the action engine's disposition-to-effectiveness walk, and
 * the intelligence reads that answer it: lessons learned and the evaluations.
 */

export interface GuideStep {
  readonly n: number;
  readonly title: string;
  readonly screen: string;
  readonly action: string;
  readonly success: string;
}

export const GUIDE_STEPS: GuideStep[] = [
  {
    n: 1,
    title: "Sign in",
    screen: "The welcome screen",
    action:
      "Choose Sign in, then sign in on the login page with the sandbox account first: qa-test plays the contractor. For the real walk, each of you signs in with your own account.",
    success: "The application opens and the dashboard greets you by your account's name.",
  },
  {
    n: 2,
    title: "Read your dashboard",
    screen: "Dashboard",
    action:
      "Read the tiles and tables the system answers for your account: the inspection and finding totals, the raised-versus-closed trend, the per-contractor dossiers, and, for an inspector, the My Work list of assigned inspections with anything past its date flagged.",
    success:
      "Every number is read from the portfolio read models for the signed-in principal. A tile the read models cannot feed is not on the screen at all, and a management account sees the same sections with nothing it may write.",
  },
  {
    n: 3,
    title: "Author, submit and approve the ITP",
    screen: "Inspection and test plans, then the plan's own page",
    action:
      "Open Inspection and test plans. As the contractor, use Create ITP: name the plan, pick one of your own contracts and a discipline. On the plan's page add its items (number, activity, method, acceptance criteria), edit or remove them while it is a draft, then choose Submit for review. Sign out, sign in as the operator, open the same plan, record a review comment, then choose Approve plan.",
    success:
      "The plan walks Draft to Submitted to Approved, the workspace freezes to read-only once submitted, the review exchange keeps your comment with your name and the time, and the plan's page now lists the approved items as planning candidates owed a date.",
  },
  {
    n: 4,
    title: "Plan the activity",
    screen: "Schedules, then the schedule's own page",
    action:
      "Open Schedules and choose the draft yearly plan. The planning form takes the candidate from the list the approved ITP fed; pick the date and the verification type, use Check the date to see the calendar before you commit, then plan the activity and walk the plan through submit, endorse and approve.",
    success:
      "The activity appears on the plan with its date, and the plan's state chip walks Draft, Submitted, Endorsed, Approved. A date the calendar refuses is refused with its own message, shown on the screen.",
  },
  {
    n: 5,
    title: "Conduct the inspection",
    screen: "Inspections & RFIs, then the record's checklist",
    action:
      "Open Inspections & RFIs and choose the distributed record you are nominated on. Begin conduct, then walk the lines: a line that carries the checklist bank's numbering renders under its numbered section and is marked YES, NO or N/A, which records as the line outcome it maps to; any line also takes the full outcome vocabulary, a comment and an attached photograph. Confirm the declaration and submit the results.",
    success:
      "The result banner names the outcome in a sentence: whether every checklist line was judged and under which rule of thumb, in plain words. The outcome distribution strip above the lines counts what you recorded.",
  },
  {
    n: 6,
    title: "See the finding born",
    screen: "Findings & Punch Lists",
    action:
      "Open Findings & Punch Lists. The line recorded as rejected has a finding of its own, born on that line. Open it and read where it came from: the record, the line, the observed description, and the phase history that shows the birth.",
    success:
      "The finding carries its own identifier and its state chip. A comment line's finding can be added to a punch list; the punch list is where the contractor completes it and the inspector accepts it.",
  },
  {
    n: 7,
    title: "Run the response and punch loops",
    screen: "The finding's page, both accounts",
    action:
      "On the negative observation awaiting a response, the contractor proposes the correction, the corrective action and the target date; the operator agrees the round. On the punch item, the contractor marks it completed with notes; the inspector accepts with a closure reference.",
    success:
      "The response thread keeps every round with its decision, and the punch item walks In punch list, Punch completed, Closed. The person who accepts a punch cannot be the person who completed it; the system refuses that.",
  },
  {
    n: 8,
    title: "Promote and run the action",
    screen: "Findings & Punch Lists, then Non-Conformities & Actions",
    action:
      "As the operator, promote the recorded negative observation to a non-conformance report. On the issued action request, the contractor proposes the disposition (Use as is needs a concession reference; without one the proposal is refused and the screen shows its text), the operator codes the root cause and approves, the contractor declares implementation with evidence, and the operator verifies closure.",
    success:
      "The action record walks Issued, Response submitted, Agreed, Implemented, Effectiveness review. The finding's own page links the action request the engine created.",
  },
  {
    n: 9,
    title: "Answer the effectiveness review",
    screen: "The action record",
    action:
      "On the record waiting for its effectiveness review, choose whether the corrective action held: Effective closes the record; Not effective offers the follow-up action the engine links back to this one.",
    success:
      "An effective answer closes the action with the root cause coded, the disposition, the implementation evidence and the review all in the phase history.",
  },
  {
    n: 10,
    title: "Mark the lesson learned",
    screen: "The closed finding's page, then Quality Lessons Learned",
    action:
      "On the closed finding, mark what it taught: a lesson status and a note. Open Quality Lessons Learned and read the register filtered by the lesson status you set.",
    success:
      "The lesson appears in the register under the status you marked. Only the operator's side can mark it; the system refuses everyone else and the screen renders that refusal.",
  },
  {
    n: 11,
    title: "Read the evaluations",
    screen: "My Evaluations",
    action:
      "Open My Evaluations and read the per-contractor tabs: the response statistics, first-time pass rate, findings by class, the aging of open NCRs and CARs, the mean days a finding stays open and the overdue actions, all from the contractor dossier the system answers.",
    success:
      "Every number is a percentage or a count from the dossier read model; the screen renders what the system returns and invents no rating on top of it.",
  },
  {
    n: 12,
    title: "Read the calendar",
    screen: "Calendar",
    action:
      "Open the Calendar and read the month grid for the signed-in principal: the scheduled activities on their dates, the period shading, and the side list of what is coming.",
    success:
      "The grid shows this principal's own slice of the plan, and the side list matches the grid.",
  },
];

export const GUIDE_INTRO_STEPS: number = GUIDE_STEPS.length;
